import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * PhonebookTab - Unified contact management for Financial Hub
 *
 * Primary source for all business contacts. Contacts can be
 * assigned as suppliers, customers, or both.
 */
import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaAddressBook, FaPhone, FaEnvelope, FaPlus, FaDownload, FaSync, FaEdit, FaTrash, FaUserTag, FaLink, FaUser, FaUsers, FaHandshake, FaTruck, FaUserCheck } from 'react-icons/fa';
import { apiClient } from '../../../utils/apiClient';
import { notify } from '../../../utils/notifications';
import mockDataService from '../../../services/mockDataService';
import TabPageLayout from './TabPageLayout';
import { StatsGrid, StatCard, StatIcon, StatLabel, StatValue, TableContainer, Table, Th, Td, Tr, FiltersContainer, FilterGroup, Label, Input, Select, Button, StatusBadge, EmptyState, EmptyStateIcon, EmptyStateText, SortableTh } from './SharedComponents';
import { CONTACT_DESIGNATION_LABELS, calculateContactStatistics, filterContacts } from '../../../types/contact';
import ContactFormModal from '../../../components/ContactFormModal';
import AssignContactModal from '../../../components/AssignContactModal';
import LinkUserModal from '../../../components/LinkUserModal';
// Designation Badge Component
const DesignationBadge = styled.span `
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => {
    switch (props.designation) {
        case 'supplier': return 'rgba(184, 134, 11, 0.15)';
        case 'customer': return 'rgba(22, 163, 74, 0.15)';
        case 'both': return 'rgba(139, 92, 246, 0.15)';
        default: return 'var(--bg-tertiary)';
    }
}};
  color: ${props => {
    switch (props.designation) {
        case 'supplier': return 'var(--accent-gold)';
        case 'customer': return '#22c55e';
        case 'both': return '#a78bfa';
        default: return 'var(--text-secondary)';
    }
}};
  border: 1px solid ${props => {
    switch (props.designation) {
        case 'supplier': return 'rgba(184, 134, 11, 0.3)';
        case 'customer': return 'rgba(22, 163, 74, 0.3)';
        case 'both': return 'rgba(139, 92, 246, 0.3)';
        default: return 'var(--border-color)';
    }
}};
`;
// Action buttons container
const ActionButtons = styled.div `
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;
const IconButton = styled.button `
  background: ${props => {
    switch (props.variant) {
        case 'primary': return 'var(--accent-gold)';
        case 'danger': return '#ef4444';
        default: return 'var(--bg-tertiary)';
    }
}};
  color: ${props => props.variant ? 'white' : 'var(--text-primary)'};
  border: 1px solid ${props => {
    switch (props.variant) {
        case 'primary': return 'var(--accent-gold)';
        case 'danger': return '#ef4444';
        default: return 'var(--border-color)';
    }
}};
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const ClickableCell = styled.span `
  color: var(--accent-gold);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    text-decoration: underline;
    color: var(--accent-gold-light);
  }
`;
const PhonebookTab = () => {
    const [contacts, setContacts] = useState([]);
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [designationFilter, setDesignationFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    // Sort state
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    // Modal state
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isLinkUserModalOpen, setIsLinkUserModalOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    // Fetch contacts from API or mock data
    const fetchContacts = useCallback(async () => {
        setIsLoading(true);
        try {
            // Check if mock mode is enabled
            if (mockDataService.isMockMode()) {
                console.log('📒 PhonebookTab: Using mock data');
                const mockContacts = mockDataService.getContacts();
                setContacts(mockContacts);
                setStatistics(calculateContactStatistics(mockContacts));
                setIsLoading(false);
                return;
            }
            // Real API call
            const response = await apiClient.get('contacts-list');
            if (response.ok && response.data?.success) {
                setContacts(response.data.contacts);
                setStatistics(response.data.statistics);
            }
            else {
                notify.error('Error', 'Failed to load contacts');
            }
        }
        catch (error) {
            console.error('Failed to fetch contacts:', error);
            notify.error('Error', 'Failed to load contacts');
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    // Handle sort
    const handleSort = (key) => {
        if (sortKey === key) {
            // Cycle through: asc -> desc -> null
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            }
            else if (sortDirection === 'desc') {
                setSortDirection(null);
                setSortKey(null);
            }
            else {
                setSortDirection('asc');
            }
        }
        else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };
    // Sort contacts
    const sortContacts = (contactsToSort) => {
        if (!sortKey || !sortDirection)
            return contactsToSort;
        return [...contactsToSort].sort((a, b) => {
            let aValue;
            let bValue;
            switch (sortKey) {
                case 'companyName':
                    aValue = a.companyName?.toLowerCase() || '';
                    bValue = b.companyName?.toLowerCase() || '';
                    break;
                case 'contactPerson':
                    aValue = a.contactPerson?.toLowerCase() || '';
                    bValue = b.contactPerson?.toLowerCase() || '';
                    break;
                case 'phone':
                    aValue = a.phone || '';
                    bValue = b.phone || '';
                    break;
                case 'email':
                    aValue = a.email?.toLowerCase() || '';
                    bValue = b.email?.toLowerCase() || '';
                    break;
                case 'location':
                    aValue = `${a.address?.city || ''} ${a.address?.state || ''}`.toLowerCase();
                    bValue = `${b.address?.city || ''} ${b.address?.state || ''}`.toLowerCase();
                    break;
                case 'designation':
                    aValue = a.designation || '';
                    bValue = b.designation || '';
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                default:
                    return 0;
            }
            if (aValue < bValue)
                return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue)
                return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };
    // Apply filters and sorting
    useEffect(() => {
        let filtered = filterContacts(contacts, {
            search: searchTerm,
            designation: designationFilter,
            status: statusFilter
        });
        // Apply sorting
        filtered = sortContacts(filtered);
        setFilteredContacts(filtered);
        // Recalculate statistics for filtered data
        if (filtered.length !== contacts.length) {
            setStatistics(calculateContactStatistics(filtered));
        }
        else if (contacts.length > 0) {
            setStatistics(calculateContactStatistics(contacts));
        }
    }, [contacts, searchTerm, designationFilter, statusFilter, sortKey, sortDirection]);
    // Initial load
    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);
    // Handle new contact
    const handleAddContact = () => {
        setSelectedContact(null);
        setIsFormModalOpen(true);
    };
    // Handle edit contact
    const handleEditContact = (contact) => {
        setSelectedContact(contact);
        setIsFormModalOpen(true);
    };
    // Handle assign contact
    const handleAssignContact = (contact) => {
        setSelectedContact(contact);
        setIsAssignModalOpen(true);
    };
    // Handle link user
    const handleLinkUser = (contact) => {
        setSelectedContact(contact);
        setIsLinkUserModalOpen(true);
    };
    // Handle link user modal close
    const handleLinkUserModalClose = (saved) => {
        setIsLinkUserModalOpen(false);
        setSelectedContact(null);
        if (saved) {
            fetchContacts();
        }
    };
    // Handle delete contact
    const handleDeleteContact = async (contact) => {
        if (contact.designation !== 'none') {
            notify.warning('Warning', 'Remove supplier/customer designation before deleting');
            return;
        }
        if (!window.confirm(`Are you sure you want to delete ${contact.companyName}?`)) {
            return;
        }
        try {
            // Check if mock mode is enabled
            if (mockDataService.isMockMode()) {
                const deleted = mockDataService.deleteContact(contact.id);
                if (deleted) {
                    notify.success('Success', 'Contact deleted successfully');
                    fetchContacts();
                }
                else {
                    notify.error('Error', 'Failed to delete contact');
                }
                return;
            }
            // Real API call
            const response = await apiClient.delete(`contacts-delete/${contact.id}`);
            if (response.ok) {
                notify.success('Success', 'Contact deleted successfully');
                fetchContacts();
            }
            else {
                notify.error('Error', 'Failed to delete contact');
            }
        }
        catch (error) {
            console.error('Failed to delete contact:', error);
            notify.error('Error', 'Failed to delete contact');
        }
    };
    // Handle form modal close
    const handleFormModalClose = (saved) => {
        setIsFormModalOpen(false);
        setSelectedContact(null);
        if (saved) {
            fetchContacts();
        }
    };
    // Handle assign modal close
    const handleAssignModalClose = (saved) => {
        setIsAssignModalOpen(false);
        setSelectedContact(null);
        if (saved) {
            fetchContacts();
        }
    };
    // Export to CSV
    const handleExportCSV = () => {
        if (filteredContacts.length === 0) {
            notify.warning('Warning', 'No contacts to export');
            return;
        }
        const headers = ['Company', 'Contact Person', 'Email', 'Phone', 'Mobile', 'City', 'State', 'Designation', 'Status'];
        const rows = filteredContacts.map(c => [
            c.companyName,
            c.contactPerson,
            c.email,
            c.phone,
            c.mobilePhone || '',
            c.address?.city || '',
            c.address?.state || '',
            CONTACT_DESIGNATION_LABELS[c.designation],
            c.status
        ]);
        const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `phonebook-export-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        notify.success('Success', 'Contacts exported successfully');
    };
    // Render stats
    const renderStats = () => (_jsxs(StatsGrid, { children: [_jsxs(StatCard, { color: "#3b82f6", children: [_jsx(StatIcon, { color: "#3b82f6", children: _jsx(FaUsers, {}) }), _jsx(StatLabel, { children: "Total Contacts" }), _jsx(StatValue, { children: statistics?.total || 0 })] }), _jsxs(StatCard, { color: "#10b981", children: [_jsx(StatIcon, { color: "#10b981", children: _jsx(FaHandshake, {}) }), _jsx(StatLabel, { children: "Customers" }), _jsx(StatValue, { children: statistics?.customersCount || 0 })] }), _jsxs(StatCard, { color: "#6366f1", children: [_jsx(StatIcon, { color: "#6366f1", children: _jsx(FaTruck, {}) }), _jsx(StatLabel, { children: "Suppliers" }), _jsx(StatValue, { children: statistics?.suppliersCount || 0 })] }), _jsxs(StatCard, { color: "#8b5cf6", children: [_jsx(StatIcon, { color: "#8b5cf6", children: _jsx(FaUserCheck, {}) }), _jsx(StatLabel, { children: "Linked Users" }), _jsx(StatValue, { children: statistics?.linkedUsersCount || 0 })] })] }));
    // Render actions
    const renderActions = () => (_jsxs(_Fragment, { children: [_jsxs(Button, { "$primary": true, onClick: handleAddContact, children: [_jsx(FaPlus, { style: { marginRight: '0.5rem' } }), " Add Contact"] }), _jsxs(Button, { onClick: handleExportCSV, children: [_jsx(FaDownload, { style: { marginRight: '0.5rem' } }), " Export CSV"] }), _jsxs(Button, { onClick: fetchContacts, disabled: isLoading, children: [_jsx(FaSync, { style: { marginRight: '0.5rem' } }), " ", isLoading ? 'Loading...' : 'Refresh'] })] }));
    // Render filters
    const renderFilters = () => (_jsxs(FiltersContainer, { children: [_jsxs(FilterGroup, { children: [_jsx(Label, { children: "Search" }), _jsx(Input, { type: "text", placeholder: "Search by name, email, phone...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsxs(FilterGroup, { children: [_jsx(Label, { children: "Designation" }), _jsxs(Select, { value: designationFilter, onChange: (e) => setDesignationFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Types" }), _jsx("option", { value: "none", children: "Unassigned" }), _jsx("option", { value: "supplier", children: "Supplier" }), _jsx("option", { value: "customer", children: "Customer" }), _jsx("option", { value: "both", children: "Both" })] })] }), _jsxs(FilterGroup, { children: [_jsx(Label, { children: "Status" }), _jsxs(Select, { value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Status" }), _jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "inactive", children: "Inactive" })] })] })] }));
    // Render table
    const renderTable = () => {
        if (filteredContacts.length === 0) {
            return (_jsxs(EmptyState, { children: [_jsx(EmptyStateIcon, { children: _jsx(FaAddressBook, {}) }), _jsx(EmptyStateText, { children: contacts.length === 0
                            ? 'No contacts yet. Add your first contact to get started.'
                            : 'No contacts match your filters.' })] }));
        }
        return (_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(SortableTh, { sortKey: "companyName", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Company" }), _jsx(SortableTh, { sortKey: "contactPerson", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Contact Person" }), _jsx(SortableTh, { sortKey: "phone", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Phone" }), _jsx(SortableTh, { sortKey: "email", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Email" }), _jsx(SortableTh, { sortKey: "location", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Location" }), _jsx(SortableTh, { sortKey: "designation", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Designation" }), _jsx(SortableTh, { sortKey: "status", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Status" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: filteredContacts.map((contact) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsx("strong", { children: contact.companyName }) }), _jsx(Td, { children: contact.contactPerson }), _jsx(Td, { children: _jsxs(ClickableCell, { onClick: () => window.open(`tel:${contact.phone}`), children: [_jsx(FaPhone, { size: 12 }), " ", contact.phone] }) }), _jsx(Td, { children: _jsxs(ClickableCell, { onClick: () => window.open(`mailto:${contact.email}`), children: [_jsx(FaEnvelope, { size: 12 }), " ", contact.email] }) }), _jsx(Td, { children: contact.address?.city && contact.address?.state
                                        ? `${contact.address.city}, ${contact.address.state}`
                                        : '-' }), _jsx(Td, { children: _jsx(DesignationBadge, { designation: contact.designation, children: CONTACT_DESIGNATION_LABELS[contact.designation] }) }), _jsx(Td, { children: _jsx(StatusBadge, { status: contact.status, children: contact.status.toUpperCase() }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsx(IconButton, { title: "Edit contact", onClick: () => handleEditContact(contact), children: _jsx(FaEdit, {}) }), _jsx(IconButton, { variant: "primary", title: "Assign as supplier/customer", onClick: () => handleAssignContact(contact), children: _jsx(FaUserTag, {}) }), _jsx(IconButton, { title: contact.userId ? 'Linked to user account' : 'Link user account', onClick: () => handleLinkUser(contact), style: contact.userId ? { color: '#10b981', background: 'rgba(16, 185, 129, 0.15)' } : {}, children: contact.userId ? _jsx(FaUser, {}) : _jsx(FaLink, {}) }), _jsx(IconButton, { variant: "danger", title: "Delete contact", onClick: () => handleDeleteContact(contact), disabled: contact.designation !== 'none', children: _jsx(FaTrash, {}) })] }) })] }, contact.id))) })] }) }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(TabPageLayout, { title: "Phonebook", subtitle: "Manage all business contacts. Assign contacts as suppliers or customers.", stats: renderStats(), actions: renderActions(), filters: renderFilters(), children: renderTable() }), isFormModalOpen && (_jsx(ContactFormModal, { contact: selectedContact, onClose: handleFormModalClose })), isAssignModalOpen && selectedContact && (_jsx(AssignContactModal, { contact: selectedContact, onClose: handleAssignModalClose })), isLinkUserModalOpen && selectedContact && (_jsx(LinkUserModal, { contact: selectedContact, onClose: handleLinkUserModalClose }))] }));
};
export default PhonebookTab;
